<template>
  <q-card style="max-width: 900px; max-height: 800px">
    <q-card-section class="row items-center">
      <span class="text-h6"
        >TRV händelse Id: {{ eventId }}
        <template v-if="reason">
          - ({{ reason }}) - ({{ signatures }})
        </template>
      </span>
      <q-space />
      <q-toggle v-model="showRawdata" label="Visa rådata" />
    </q-card-section>
    <q-separator />
    <q-card-section class="q-pa-none">
      <table v-if="mpkData" style="table-layout: fixed; width: 100%">
        <tbody>
          <tr v-if="operativeEventDescription">
            <td
              style="
                width: 200px;
                background: #f2f2f2;
                font-weight: bold;
                border-right: 1px solid rgba(0, 0, 0, 0.1);
                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
              "
              class="q-px-md q-py-xs"
            >
              Operative Event Description
            </td>
            <td style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)">
              <div style="overflow: auto; max-height: 200px" class="q-px-md">
                {{ operativeEventDescription }}
              </div>
            </td>
          </tr>

          <tr v-if="railwayInternalEventInformation.length">
            <td
              style="
                width: 200px;
                background: #f2f2f2;
                font-weight: bold;
                border-right: 1px solid rgba(0, 0, 0, 0.1);
                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
              "
              class="q-px-md q-py-xs"
            >
              Intern information
            </td>
            <td style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)">
              <div
                style="overflow: auto; max-height: 200px; white-space: pre-wrap"
                class="q-px-md"
              >
                {{ railwayInternalEventInformation }}
              </div>
            </td>
          </tr>

          <tr
            v-for="(publicMessage, $i) of trafficMessagePublicMessage"
            :key="$i"
          >
            <td
              style="
                width: 200px;
                background: #f2f2f2;
                font-weight: bold;
                border-right: 1px solid rgba(0, 0, 0, 0.1);
                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
              "
              class="q-px-md q-py-xs"
            >
              {{ publicMessage.label }}
            </td>
            <td style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)">
              <div
                style="overflow: auto; max-height: 200px"
                class="q-px-md column"
              >
                <div class="text-weight-bold">
                  {{ publicMessage.header }}
                </div>
                {{ publicMessage.value }}
              </div>
            </td>
          </tr>
          <template v-for="h in headers" :key="h.key">
            <tr v-if="mpkData && mpkData[h.key]">
              <td
                style="
                  width: 200px;
                  background: #f2f2f2;
                  font-weight: bold;
                  border-right: 1px solid rgba(0, 0, 0, 0.1);
                  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                "
                class="q-px-md q-py-xs"
              >
                {{ h.label }}
              </td>
              <td style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)">
                <div style="overflow: auto; max-height: 200px" class="q-px-md">
                  {{ mpkData[h.key] }}
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="showRawdata">
            <td
              style="
                width: 200px;
                vertical-align: top;
                background: #f2f2f2;
                font-weight: bold;
                border-right: 1px solid rgba(0, 0, 0, 0.1);
                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
              "
              class="q-px-md q-py-xs"
            >
              Rådata
            </td>
            <td style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)">
              <pre
                style="
                  overflow: auto;
                  max-height: 400px;
                  margin: 0;
                  font-size: 11px;
                  color: #444;
                "
                class="q-pa-md"
              >
                  
                  {{ JSON.stringify(mpkData, null, 2).trim() }}
                
                </pre
              >
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="flex items-center justify-center q-pa-xl text-grey-6">
        Ingen data hittades...
      </div>
    </q-card-section>

    <q-separator />
    <q-card-actions align="right">
      <q-btn flat label="Stäng" @click="modal.closeModal" type="button" />
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import { UseModal } from '@/composable/useModal'
import { computed, defineComponent, inject, ref } from 'vue'
import { DeviationDelayMpkModalData } from '@/types/deviation-delay-mpk-modal-data'
import { format } from 'date-fns'

export default defineComponent({
  name: 'DeviationDelayMPKModal',

  setup() {
    const showRawdata = ref(false)
    const modal =
      inject<UseModal<DeviationDelayMpkModalData>>('delay-mpk-modal')!
    const { eventId, mpkData } = modal.state.data!

    const signatures = computed(() => {
      const data = mpkData as any as {
        SelectedSection: {
          FromLocation: { AdvertisedLocationName: string; Signature: string }
        }[]
      }
      if (!data.SelectedSection) return '-'

      return data.SelectedSection.map(
        (x) => x.FromLocation.AdvertisedLocationName || x.FromLocation.Signature
      ).join(', ')
    })

    const reason = computed(() => {
      const data = mpkData as any as {
        ReasonCode: string
        ReasonInternalDescription?: string
      }

      return data.ReasonInternalDescription || data.ReasonCode
    })

    const operativeEventDescription = computed(() => {
      const data = mpkData as any as {
        OperativeEvent: { EventType: { Description: string } }
      }
      if (!data.OperativeEvent) return ''

      return data.OperativeEvent.EventType.Description
    })

    const railwayInternalEventInformation = computed(() => {
      const data = mpkData as any as {
        RailwayInternalEventInformation: string
      }

      if (!data.RailwayInternalEventInformation) return ''

      return data.RailwayInternalEventInformation.replace(/\r\n?/g, '\n')
        .replace(/\n+/g, '\n')
        .replace(/\n/g, '\n\n')
    })

    const trafficMessagePublicMessage = computed(() => {
      const data = mpkData as any as {
        OperativeEvent: {
          TrafficImpact: {
            PublicMessage: {
              Description: string
              Header: string
              ModifiedDateTime: string
            }
          }[]
        }
      }
      if (
        !data.OperativeEvent ||
        !data.OperativeEvent.TrafficImpact ||
        !data.OperativeEvent.TrafficImpact.length
      )
        return []

      return data.OperativeEvent.TrafficImpact.filter(
        (x) => x.PublicMessage
      ).map((x) => ({
        label: `Operative Event - Traffic Impact - PublicMessage`,
        header: `${format(
          new Date(x.PublicMessage.ModifiedDateTime),
          'yyyy-MM-dd HH:mm:ss'
        )} - ${x.PublicMessage.Header}`,
        value: x.PublicMessage.Description,
      }))
    })

    const headers = [
      {
        label: 'Rubrik',
        key: 'Header',
      },
      {
        label: 'Händelsebeskrivning (intern)',
        key: 'InternalText',
      },
      {
        label: 'Händelsebeskrivning (extern)',
        key: 'ExternalDescription',
      },
    ]

    return {
      modal,
      eventId,
      mpkData,
      headers,
      showRawdata,
      signatures,
      operativeEventDescription,
      trafficMessagePublicMessage,
      railwayInternalEventInformation,
      reason,
    }
  },
})
</script>

<style>
.break {
  margin-top: 16px;
}
</style>
